export const SITE_PUBLIC_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

export const SITE_PUBLIC_BLOCK_TYPES = [
  { value: 'text', label: 'ข้อความ' },
  { value: 'image', label: 'รูปภาพ' },
] as const;

export type SitePublicBlockType = 'text' | 'image';

export type SitePublicSection = 'overview' | 'hourly_rate' | 'monthly' | 'other';

export const SITE_PUBLIC_SECTION_OPTIONS: { value: SitePublicSection; label: string }[] = [
  { value: 'overview', label: 'ภาพรวม' },
  { value: 'hourly_rate', label: 'อัตราค่าจอด' },
  { value: 'monthly', label: 'สมาชิกรายเดือน' },
  { value: 'other', label: 'อื่น ๆ' },
];

export type SitePublicImageCategory = 'parking' | 'surrounding' | 'entrance' | 'facility';

export const SITE_PUBLIC_IMAGE_CATEGORIES: { value: SitePublicImageCategory; label: string; icon: string }[] = [
  { value: 'parking', label: 'ลานจอด', icon: 'local_parking' },
  { value: 'surrounding', label: 'บริเวณรอบลาน', icon: 'location_city' },
  { value: 'entrance', label: 'ทางเข้า-ออก', icon: 'login' },
  { value: 'facility', label: 'สิ่งอำนวยความสะดวก', icon: 'room_service' },
];

export const SITE_PUBLIC_MAX_GALLERY_IMAGES = 30;

export const SITE_PUBLIC_EV_AMENITY = 'ev_charger';

/** ต้องตรงกับ SitePublicAmenityCatalog ฝั่ง API + LIFF */
export const SITE_PUBLIC_AMENITIES: { code: string; labelTh: string; labelEn: string; icon: string; isSystem?: boolean }[] = [
  { code: 'ev_charger', labelTh: 'EV Charger', labelEn: 'EV Charger', icon: 'ev_station', isSystem: true },
  { code: 'security_guard', labelTh: 'รปภ.', labelEn: 'Security guard', icon: 'security' },
  { code: 'cctv', labelTh: 'กล้องวงจรปิด', labelEn: 'CCTV', icon: 'videocam' },
  { code: 'staff', labelTh: 'มีเจ้าหน้าที่ดูแล', labelEn: 'On-site staff', icon: 'support_agent' },
  { code: 'monthly_pass', labelTh: 'บัตรรายเดือน', labelEn: 'Monthly pass', icon: 'card_membership' },
  { code: 'open_24h', labelTh: 'เปิด 24 ชม.', labelEn: 'Open 24 hours', icon: 'schedule' },
  { code: 'covered', labelTh: 'จอดในร่ม', labelEn: 'Covered parking', icon: 'roofing' },
  { code: 'motorcycle', labelTh: 'ที่จอดมอเตอร์ไซค์', labelEn: 'Motorcycle parking', icon: 'two_wheeler' },
  { code: 'accessible', labelTh: 'ที่จอดผู้พิการ', labelEn: 'Accessible parking', icon: 'accessible' },
  { code: 'lady_parking', labelTh: 'ที่จอดสุภาพสตรี', labelEn: 'Ladies parking', icon: 'female' },
  { code: 'lighting', labelTh: 'แสงสว่างเพียงพอ', labelEn: 'Well lit', icon: 'lightbulb' },
  { code: 'cashless', labelTh: 'จ่ายแบบไม่ใช้เงินสด', labelEn: 'Cashless payment', icon: 'qr_code' },
  { code: 'restroom', labelTh: 'ห้องน้ำ', labelEn: 'Restroom', icon: 'wc' },
  { code: 'near_transit', labelTh: 'ใกล้รถไฟฟ้า', labelEn: 'Near BTS/MRT', icon: 'train' },
];

export interface SitePublicImage {
  uiKey: string;
  id: number;
  category: SitePublicImageCategory;
  image: string;
  imagePath: string;
  imageExt: string;
  captionTh: string;
  captionEn: string;
  sortOrder: number;
  isActive: boolean;
  pendingBase64: string | null;
  pendingFileName: string | null;
}

export interface SitePublicAmenitySelection {
  code: string;
  noteTh: string;
  noteEn: string;
  sortOrder: number;
}

export interface SitePublicListItem {
  id: number;
  siteId: number;
  siteName: string;
  siteIsOnline: boolean;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  coverImage: string;
  sortOrder: number;
  isPublished: boolean;
  isActive: boolean;
  blockCount: number;
  imageCount: number;
}

export interface SitePublicBlock {
  uiKey: string;
  id: number;
  sortOrder: number;
  section: SitePublicSection;
  blockType: SitePublicBlockType;
  titleTh: string;
  titleEn: string;
  bodyTh: string;
  bodyEn: string;
  image: string;
  imagePath: string;
  imageExt: string;
  isActive: boolean;
  pendingBase64: string | null;
  pendingFileName: string | null;
}

export interface SitePublicDetail {
  id: number;
  siteId: number;
  siteName: string;
  siteIsOnline: boolean;
  titleTh: string;
  titleEn: string;
  summaryTh: string;
  summaryEn: string;
  addressTh: string;
  addressEn: string;
  coverImage: string;
  coverImagePath: string;
  coverImageExt: string;
  sortOrder: number;
  isPublished: boolean;
  isActive: boolean;
  blocks: SitePublicBlock[];
  coverPendingBase64: string | null;
  coverPendingFileName: string | null;
  images: SitePublicImage[];
  amenities: SitePublicAmenitySelection[];
  siteHasEv: boolean;
}

export interface SitePublicTableResponse {
  total?: number;
  Total?: number;
  page?: number;
  Page?: number;
  pageSize?: number;
  PageSize?: number;
  items?: any[];
  Items?: any[];
}

export interface SitePublicSiteOption {
  siteId: number;
  name: string;
  isOnline: boolean;
  hasProfile: boolean;
  hasEv: boolean;
  defaultAmenityCodes: string[];
}

export interface SitePublicSavePayload {
  userId: number;
  menuId: number;
  id: number;
  siteId: number;
  titleTh: string;
  titleEn?: string | null;
  summaryTh?: string | null;
  summaryEn?: string | null;
  addressTh?: string | null;
  addressEn?: string | null;
  coverImageBase64?: string | null;
  coverImagePath?: string | null;
  coverImageExt?: string | null;
  sortOrder: number;
  isPublished: boolean;
  isActive: boolean;
  blocks: Array<{
    id: number;
    sortOrder: number;
    section: string;
    blockType: string;
    titleTh?: string | null;
    titleEn?: string | null;
    bodyTh?: string | null;
    bodyEn?: string | null;
    imageBase64?: string | null;
    imagePath?: string | null;
    imageExt?: string | null;
    isActive: boolean;
  }>;
  images: Array<{
    id: number;
    category: string;
    imagePath: string;
    imageExt: string;
    captionTh?: string | null;
    captionEn?: string | null;
    sortOrder: number;
    isActive: boolean;
  }>;
  amenities: Array<{
    code: string;
    noteTh?: string | null;
    noteEn?: string | null;
    sortOrder: number;
  }>;
}

function newUiKey(): string {
  return `sp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function normalizeSection(raw: any): SitePublicSection {
  const s = String(raw ?? '').toLowerCase();
  if (s === 'overview' || s === 'hourly_rate' || s === 'monthly' || s === 'other') {
    return s;
  }
  return 'other';
}

export function createEmptySitePublicBlock(sortOrder = 10): SitePublicBlock {
  return {
    uiKey: newUiKey(),
    id: 0,
    sortOrder,
    section: 'other',
    blockType: 'text',
    titleTh: '',
    titleEn: '',
    bodyTh: '',
    bodyEn: '',
    image: '',
    imagePath: '',
    imageExt: '',
    isActive: true,
    pendingBase64: null,
    pendingFileName: null,
  };
}

export function createEmptySitePublicDetail(): SitePublicDetail {
  return {
    id: 0,
    siteId: 0,
    siteName: '',
    siteIsOnline: false,
    titleTh: '',
    titleEn: '',
    summaryTh: '',
    summaryEn: '',
    addressTh: '',
    addressEn: '',
    coverImage: '',
    coverImagePath: '',
    coverImageExt: '',
    sortOrder: 0,
    isPublished: false,
    isActive: true,
    blocks: [createEmptySitePublicBlock(10)],
    coverPendingBase64: null,
    coverPendingFileName: null,
    images: [],
    amenities: [],
    siteHasEv: false,
  };
}

export function createSitePublicImage(
  category: SitePublicImageCategory,
  sortOrder: number,
  base64: string,
  fileName: string,
): SitePublicImage {
  return {
    uiKey: newUiKey(),
    id: 0,
    category,
    image: '',
    imagePath: '',
    imageExt: '',
    captionTh: '',
    captionEn: '',
    sortOrder,
    isActive: true,
    pendingBase64: base64,
    pendingFileName: fileName,
  };
}

function normalizeImageCategory(raw: any): SitePublicImageCategory {
  const s = String(raw ?? '').toLowerCase();
  return SITE_PUBLIC_IMAGE_CATEGORIES.some((c) => c.value === s)
    ? (s as SitePublicImageCategory)
    : 'parking';
}

export function mapSitePublicImage(raw: any): SitePublicImage {
  return {
    uiKey: newUiKey(),
    id: Number(raw?.id ?? raw?.Id ?? 0),
    category: normalizeImageCategory(raw?.category ?? raw?.Category),
    image: String(raw?.image ?? raw?.Image ?? ''),
    imagePath: String(raw?.imagePath ?? raw?.ImagePath ?? ''),
    imageExt: String(raw?.imageExt ?? raw?.ImageExt ?? ''),
    captionTh: String(raw?.captionTh ?? raw?.CaptionTh ?? ''),
    captionEn: String(raw?.captionEn ?? raw?.CaptionEn ?? ''),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
    pendingBase64: null,
    pendingFileName: null,
  };
}

export function mapSitePublicAmenity(raw: any): SitePublicAmenitySelection {
  return {
    code: String(raw?.code ?? raw?.Code ?? '').toLowerCase(),
    noteTh: String(raw?.noteTh ?? raw?.NoteTh ?? ''),
    noteEn: String(raw?.noteEn ?? raw?.NoteEn ?? ''),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
  };
}

export function imagePreviewSrc(image: SitePublicImage): string {
  return image.pendingBase64 || image.image || '';
}

export function imageCategoryLabel(category: string): string {
  return SITE_PUBLIC_IMAGE_CATEGORIES.find((c) => c.value === category)?.label ?? category;
}

export function mapSitePublicListItem(raw: any): SitePublicListItem {
  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    siteId: Number(raw?.siteId ?? raw?.SiteId ?? 0),
    siteName: String(raw?.siteName ?? raw?.SiteName ?? ''),
    siteIsOnline: Boolean(raw?.siteIsOnline ?? raw?.SiteIsOnline ?? false),
    titleTh: String(raw?.titleTh ?? raw?.TitleTh ?? ''),
    titleEn: String(raw?.titleEn ?? raw?.TitleEn ?? ''),
    summaryTh: String(raw?.summaryTh ?? raw?.SummaryTh ?? ''),
    summaryEn: String(raw?.summaryEn ?? raw?.SummaryEn ?? ''),
    coverImage: String(raw?.coverImage ?? raw?.CoverImage ?? ''),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    isPublished: Boolean(raw?.isPublished ?? raw?.IsPublished ?? false),
    isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
    blockCount: Number(raw?.blockCount ?? raw?.BlockCount ?? 0),
    imageCount: Number(raw?.imageCount ?? raw?.ImageCount ?? 0),
  };
}

export function mapSitePublicBlock(raw: any): SitePublicBlock {
  const type = String(raw?.blockType ?? raw?.BlockType ?? 'text').toLowerCase();
  return {
    uiKey: newUiKey(),
    id: Number(raw?.id ?? raw?.Id ?? 0),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    section: normalizeSection(raw?.section ?? raw?.Section),
    blockType: type === 'image' ? 'image' : 'text',
    titleTh: String(raw?.titleTh ?? raw?.TitleTh ?? ''),
    titleEn: String(raw?.titleEn ?? raw?.TitleEn ?? ''),
    bodyTh: String(raw?.bodyTh ?? raw?.BodyTh ?? ''),
    bodyEn: String(raw?.bodyEn ?? raw?.BodyEn ?? ''),
    image: String(raw?.image ?? raw?.Image ?? ''),
    imagePath: String(raw?.imagePath ?? raw?.ImagePath ?? ''),
    imageExt: String(raw?.imageExt ?? raw?.ImageExt ?? ''),
    isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
    pendingBase64: null,
    pendingFileName: null,
  };
}

export function mapSitePublicDetail(raw: any): SitePublicDetail {
  const blocksRaw = raw?.blocks ?? raw?.Blocks ?? [];
  const imagesRaw = raw?.images ?? raw?.Images ?? [];
  const amenitiesRaw = raw?.amenities ?? raw?.Amenities ?? [];
  return {
    id: Number(raw?.id ?? raw?.Id ?? 0),
    siteId: Number(raw?.siteId ?? raw?.SiteId ?? 0),
    siteName: String(raw?.siteName ?? raw?.SiteName ?? ''),
    siteIsOnline: Boolean(raw?.siteIsOnline ?? raw?.SiteIsOnline ?? false),
    titleTh: String(raw?.titleTh ?? raw?.TitleTh ?? ''),
    titleEn: String(raw?.titleEn ?? raw?.TitleEn ?? ''),
    summaryTh: String(raw?.summaryTh ?? raw?.SummaryTh ?? ''),
    summaryEn: String(raw?.summaryEn ?? raw?.SummaryEn ?? ''),
    addressTh: String(raw?.addressTh ?? raw?.AddressTh ?? ''),
    addressEn: String(raw?.addressEn ?? raw?.AddressEn ?? ''),
    coverImage: String(raw?.coverImage ?? raw?.CoverImage ?? ''),
    coverImagePath: String(raw?.coverImagePath ?? raw?.CoverImagePath ?? ''),
    coverImageExt: String(raw?.coverImageExt ?? raw?.CoverImageExt ?? ''),
    sortOrder: Number(raw?.sortOrder ?? raw?.SortOrder ?? 0),
    isPublished: Boolean(raw?.isPublished ?? raw?.IsPublished ?? false),
    isActive: Boolean(raw?.isActive ?? raw?.IsActive ?? true),
    blocks: Array.isArray(blocksRaw) ? blocksRaw.map(mapSitePublicBlock) : [],
    coverPendingBase64: null,
    coverPendingFileName: null,
    images: Array.isArray(imagesRaw) ? imagesRaw.map(mapSitePublicImage) : [],
    amenities: Array.isArray(amenitiesRaw) ? amenitiesRaw.map(mapSitePublicAmenity) : [],
    siteHasEv: Boolean(raw?.siteHasEv ?? raw?.SiteHasEv ?? false),
  };
}

export function mapSitePublicSiteOption(raw: any): SitePublicSiteOption {
  const defaults = raw?.defaultAmenityCodes ?? raw?.DefaultAmenityCodes ?? [];
  return {
    siteId: Number(raw?.siteId ?? raw?.SiteId ?? 0),
    name: String(raw?.name ?? raw?.Name ?? ''),
    isOnline: Boolean(raw?.isOnline ?? raw?.IsOnline ?? false),
    hasProfile: Boolean(raw?.hasProfile ?? raw?.HasProfile ?? false),
    hasEv: Boolean(raw?.hasEv ?? raw?.HasEv ?? false),
    defaultAmenityCodes: Array.isArray(defaults) ? defaults.map((c: any) => String(c).toLowerCase()) : [],
  };
}

export function toSitePublicSavePayload(
  detail: SitePublicDetail,
  userId: number,
  menuId: number,
): SitePublicSavePayload {
  return {
    userId,
    menuId,
    id: detail.id,
    siteId: detail.siteId,
    titleTh: detail.titleTh.trim(),
    titleEn: detail.titleEn?.trim() || null,
    summaryTh: detail.summaryTh?.trim() || null,
    summaryEn: detail.summaryEn?.trim() || null,
    addressTh: detail.addressTh?.trim() || null,
    addressEn: detail.addressEn?.trim() || null,
    coverImageBase64: detail.coverPendingBase64,
    coverImagePath: detail.coverImagePath || null,
    coverImageExt: detail.coverImageExt || null,
    sortOrder: detail.sortOrder,
    isPublished: detail.isPublished,
    isActive: detail.isActive,
    blocks: detail.blocks.map((b, i) => ({
      id: b.id,
      sortOrder: b.sortOrder > 0 ? b.sortOrder : (i + 1) * 10,
      section: b.section,
      blockType: b.blockType,
      titleTh: b.titleTh?.trim() || null,
      titleEn: b.titleEn?.trim() || null,
      bodyTh: b.bodyTh?.trim() || null,
      bodyEn: b.bodyEn?.trim() || null,
      imageBase64: b.pendingBase64,
      imagePath: b.imagePath || null,
      imageExt: b.imageExt || null,
      isActive: b.isActive,
    })),
    images: detail.images.map((img, i) => ({
      id: img.id,
      category: img.category,
      imagePath: img.imagePath,
      imageExt: img.imageExt,
      captionTh: img.captionTh?.trim() || null,
      captionEn: img.captionEn?.trim() || null,
      sortOrder: (i + 1) * 10,
      isActive: img.isActive,
    })),
    amenities: detail.amenities
      .filter((a) => a.code !== SITE_PUBLIC_EV_AMENITY || (detail.siteHasEv && (a.noteTh.trim() || a.noteEn.trim())))
      .map((a, i) => ({
        code: a.code,
        noteTh: a.noteTh?.trim() || null,
        noteEn: a.noteEn?.trim() || null,
        sortOrder: (i + 1) * 10,
      })),
  };
}

export function coverPreviewSrc(detail: SitePublicDetail): string {
  return detail.coverPendingBase64 || detail.coverImage || '';
}

export function blockPreviewSrc(block: SitePublicBlock): string {
  return block.pendingBase64 || block.image || '';
}

export function sectionLabel(section: string): string {
  return SITE_PUBLIC_SECTION_OPTIONS.find((s) => s.value === section)?.label ?? section;
}
